import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign, Users, FileText, Package, TrendingUp, TrendingDown,
  ArrowUp, ArrowDown, Activity, AlertTriangle, CheckCircle2, ShoppingBag,
  CreditCard, Truck, Target
} from 'lucide-react';

const OverviewTab = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const quickStats = [
    { id: 'revenue', label: 'Revenue (MTD)', value: 'SAR 4.2M', change: '+12.5%', trend: 'up', icon: DollarSign, color: 'teal' },
    { id: 'orders', label: 'Orders Open', value: '248', change: '+8%', trend: 'up', icon: FileText, color: 'blue' },
    { id: 'customers', label: 'Active B2B Customers', value: '89', change: '+3', trend: 'up', icon: Users, color: 'indigo' },
    { id: 'credit-util', label: 'Credit Utilized', value: 'SAR 1.8M', change: '-2.1%', trend: 'down', icon: CreditCard, color: 'amber' },
    { id: 'atp-orders', label: 'ATP Fulfillment', value: '96%', change: '+1.2%', trend: 'up', icon: Package, color: 'green' },
    { id: 'collections', label: 'Overdue (90+ days)', value: 'SAR 340K', change: '-5%', trend: 'down', icon: AlertTriangle, color: 'red' },
  ];

  const recentOrders = [
    { id: 'SO-2024-0892', customer: 'Al Rajhi Trading', amount: 'SAR 125,000', status: 'Dispatched', time: '2 hours ago' },
    { id: 'SO-2024-0891', customer: 'Gulf Distributors', amount: 'SAR 89,500', status: 'Packed', time: '4 hours ago' },
    { id: 'SO-2024-0890', customer: 'Saudi Foods Co', amount: 'SAR 234,000', status: 'Processing', time: '6 hours ago' },
    { id: 'SO-2024-0889', customer: 'Metro Retail', amount: 'SAR 67,200', status: 'Delivered', time: '1 day ago' },
  ];

  const alerts = [
    { type: 'credit', title: 'Credit hold – ABC Corp', desc: 'Overdue invoices exceed limit', time: '1 hour ago' },
    { type: 'stock', title: 'Low ATP – Item SKU-8821', desc: 'Suggest substitution SKU-8822', time: '3 hours ago' },
    { type: 'restock', title: 'Consumption alert – Gulf Distributors', desc: 'Typical order window in 5 days', time: '5 hours ago' },
  ];

  const getColorClasses = (color) => {
    const map = { teal: 'bg-teal-600 text-white', blue: 'bg-blue-600 text-white', indigo: 'bg-indigo-600 text-white', amber: 'bg-amber-600 text-white', green: 'bg-green-600 text-white', red: 'bg-red-600 text-white' };
    return map[color] || map.teal;
  };

  const getStatusColor = (status) => {
    if (status === 'Delivered') return 'bg-green-100 text-green-700';
    if (status === 'Dispatched') return 'bg-blue-100 text-blue-700';
    if (status === 'Packed') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Sales Overview</h2>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        >
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickStats.map((stat) => (
          <motion.div
            key={stat.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-3 rounded-xl ${getColorClasses(stat.color)}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              <div className={`flex items-center text-sm font-medium ${stat.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {stat.trend === 'up' ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
                {stat.change}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {recentOrders.map((order) => (
                <div key={order.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{order.id}</p>
                    <p className="text-sm text-gray-500">{order.customer}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{order.amount}</p>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>{order.status}</span>
                  </div>
                  <p className="text-xs text-gray-400">{order.time}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Alerts</h3>
              <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">{alerts.length}</span>
            </div>
            <div className="p-4 space-y-3">
              {alerts.map((a, i) => (
                <div key={i} className="p-3 border border-gray-100 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{a.title}</p>
                      <p className="text-xs text-gray-600">{a.desc}</p>
                      <p className="text-xs text-gray-400 mt-1">{a.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'New Order', icon: FileText, color: 'teal' },
              { label: 'Log Payment', icon: CreditCard, color: 'green' },
              { label: 'Generate SOA', icon: FileText, color: 'blue' },
              { label: 'View ATP', icon: Package, color: 'indigo' },
            ].map((action, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-teal-300 hover:bg-teal-50/50 transition-all"
              >
                <div className={`p-3 rounded-xl ${getColorClasses(action.color)} mb-2`}>
                  <action.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-medium text-gray-900">{action.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
